import React, { useState, useCallback } from 'react';
import { MediaType } from '../../../types/task.types';
import './FormatSheet.css';

export interface FormatSheetProps {
  mediaTypes: MediaType[];
  taskName?: string;
  onSelect: (type: string) => void;
  onClose: () => void;
}

const FORMAT_META: Record<string, { icon: React.ReactNode; label: string; description: string }> = {
  audio: {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <rect x="9" y="2" width="6" height="11" rx="3" />
        <path d="M5 10a7 7 0 0 0 14 0M12 19v3M9 22h6" />
      </svg>
    ),
    label: 'Audio',
    description: 'Record your voice',
  },
  photo: {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21,15 16,10 5,21" />
      </svg>
    ),
    label: 'Photo',
    description: 'Take or upload an image',
  },
  video: {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <polygon points="23,7 16,12 23,17" />
        <rect x="1" y="5" width="15" height="14" rx="2" />
      </svg>
    ),
    label: 'Video',
    description: 'Record with your camera',
  },
  text: {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14,2 14,8 20,8" />
      </svg>
    ),
    label: 'Text',
    description: 'Type your response',
  },
  screen: {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <rect width="20" height="14" x="2" y="3" rx="2" />
        <line x1="8" x2="16" y1="21" y2="21" />
        <line x1="12" x2="12" y1="17" y2="21" />
      </svg>
    ),
    label: 'Screen Recording',
    description: 'Record your screen',
  },
};

export const FormatSheet: React.FC<FormatSheetProps> = ({ mediaTypes, taskName, onSelect, onClose }) => {
  const [closing, setClosing] = useState(false);

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(onClose, 280);
  }, [onClose]);

  return (
    <>
      <div className={`fs-backdrop${closing ? ' fs-closing' : ''}`} onClick={handleClose} />
      <div className={`fs-sheet${closing ? ' fs-closing' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="fs-handle"><div className="fs-handle-bar" /></div>

        <div className="fs-header">
          <div className="fs-header-text">
            <span className="fs-eyebrow">Choose format</span>
            <span className="fs-title">How would you like to respond?</span>
          </div>
          <button className="fs-close" onClick={handleClose} aria-label="Close format picker">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round">
              <line x1="1" y1="1" x2="13" y2="13" />
              <line x1="13" y1="1" x2="1" y2="13" />
            </svg>
          </button>
        </div>

        <div className="fs-options">
          {mediaTypes.map((type) => {
            const meta = FORMAT_META[type];
            if (!meta) return null;
            return (
              <button
                key={type}
                className="fs-option"
                onClick={() => onSelect(type)}
              >
                <span className={`fs-option-icon fs-icon-${type}`}>{meta.icon}</span>
                <div className="fs-option-text">
                  <span className="fs-option-label">{meta.label}</span>
                  <span className="fs-option-desc">{meta.description}</span>
                </div>
                <svg className="fs-option-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
};
